import { DynamicIcon } from './DynamicIcon';
import { cn } from '@/lib/utils';
import type { TabState } from '@/types';

interface TabBarProps {
  tabs: TabState[];
  activeTabId: string | null;
  onActivate: (tabId: string) => void;
  onClose: (tabId: string) => void;
}

export function TabBar({ tabs, activeTabId, onActivate, onClose }: TabBarProps) {
  if (tabs.length === 0) return null;

  return (
    <div data-tabbar className="flex h-9 flex-shrink-0 items-end gap-0 overflow-x-auto border-t-[calc(--spacing(0.75))] border-t-primary border-b border-border bg-tab-bar-bg">
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;
        const tabBaseClass = cn(
          'group flex h-full items-center gap-1.5 rounded-t border-r border-border text-xs select-none text-foreground',
          isActive
            ? 'bg-tab-active-bg font-semibold'
            : 'bg-tab-inactive-bg opacity-60 hover:opacity-80',
        );
        return (
          <div key={tab.id} className={tabBaseClass}>
            <button
              type="button"
              onClick={() => onActivate(tab.id)}
              className="flex cursor-pointer items-center gap-1.5 px-3 py-1.5"
            >
              {/* D-118: Home keeps its tab icon as the one tab that isn't a nav item; every
                  other tab (opened from a nav item, which already shows no leaf icon) carries
                  none. */}
              {tab.id === 'home' && (
                <DynamicIcon name={tab.icon} className="h-3.5 w-3.5 flex-shrink-0" />
              )}
              <span className="max-w-30 truncate">{tab.label}</span>
            </button>

            {tab.isCloseable && (
              <button
                type="button"
                aria-label="Close tab"
                onClick={() => onClose(tab.id)}
                className="mr-2 flex h-4 w-4 items-center justify-center rounded opacity-[0.3] hover:bg-muted group-hover:opacity-[0.65]"
              >
                <DynamicIcon name="x" className="h-3 w-3" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
